import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import { cn } from '@/utils/cn';
import { Badge } from '@/components/ui/badge';
import { focusGlow } from '@/utils/focus';

export function PageHeader({
  title,
  description,
  actions,
  // Renders inline next to the title text (e.g. a help "?" icon button).
  // Use this for affordances that semantically belong to the title rather
  // than the right-aligned action area.
  titleAction,
  // Renders a "New" badge next to the title for recently shipped features.
  isNew,
  // When set, renders a large back arrow to the left of the title linking to
  // this route (detail pages → their list page).
  backTo,
  children,
  className,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  titleAction?: ReactNode;
  isNew?: boolean;
  backTo?: string;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'border-border border-b px-8 pt-4',
        !children && 'pb-6',
        className,
      )}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="flex min-w-0 items-center gap-3">
          {backTo && (
            <Link
              to={backTo}
              aria-label="Back"
              className={cn(
                'text-muted-foreground hover:text-foreground hover:bg-accent flex size-9 shrink-0 items-center justify-center rounded transition-colors',
                focusGlow,
              )}
            >
              <ArrowLeftIcon className="size-6" />
            </Link>
          )}
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-xl font-bold tracking-tight">
                {title}
              </h1>
              {isNew && <Badge className="text-xs">New</Badge>}
              {titleAction}
            </div>
            {description && (
              <p className="text-muted-foreground text-sm">{description}</p>
            )}
          </div>
        </div>
        {actions && (
          <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>
        )}
      </div>
      {children && <div className="mt-9">{children}</div>}
    </div>
  );
}
